import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Provider } from '../../interfaces/providers-interface';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'lib-providers-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    TranslatePipe,
  ],
  templateUrl: './providers-table.component.html',
  styleUrl: './providers-table.component.css',
})
export class ProvidersTableComponent {
  @Input() providers: Provider[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() first = 0;
  @Input() rows = 10;

  @Output() pageChange = new EventEmitter<{ page: number; rows: number }>();
  @Output() toggleStatus = new EventEmitter<{ provider: Provider; status: boolean }>();
  @Output() edit = new EventEmitter<Provider>();
  @Output() delete = new EventEmitter<Provider>();

  onEdit(provider: Provider) {
    if (provider && provider.id) {
      this.edit.emit(provider);
    }
  }

  onToggleStatus(provider: Provider) {
    if (!provider.is_active && !this.hasRequiredProviderFields(provider)) {
      return;
    }
    this.toggleStatus.emit({ provider, status: !provider.is_active });
  }

  hasRequiredProviderFields(provider: Provider): boolean {
    return (
      this.hasValue(provider?.country) &&
      this.hasValue(provider?.city) &&
      this.hasValue(provider?.comune) &&
      this.hasValue(provider?.address) &&
      this.hasValue(provider?.email) &&
      this.hasValue(provider?.ruc ?? provider?.RUC) &&
      this.hasValue(provider?.business_name)
    );
  }

  canToggleStatus(provider: Provider): boolean {
    if (provider.is_active) {
      return true;
    }
    return this.hasRequiredProviderFields(provider);
  }

  getProviderStatusClass(isActive?: boolean): string {
    if (isActive) {
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800';
    }
    return 'bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800';
  }

  getCoesStatusClass(coes?: string | null): string {
    if ((coes ?? '').toUpperCase() === 'COES') {
      return 'bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-800';
    }
    return 'bg-gray-100 text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
  }

  getRoleClass(role?: string | null): string {
    const normalized = (role ?? '').trim().toLowerCase();
    if (normalized.includes('admin')) {
      return 'bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-800';
    }
    if (normalized.includes('analista') || normalized.includes('editor')) {
      return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800';
    }
    return 'bg-purple-100 text-purple-600 ring-1 ring-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:ring-purple-800';
  }

  onPageChange(event: TableLazyLoadEvent) {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.rows;
    const page = Math.floor(first / rows) + 1;
    this.pageChange.emit({ page, rows });
  }

  private hasValue(value: string | null | undefined): boolean {
    return Boolean(value?.trim());
  }
}
